import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../components/contexts/AuthContext";
import {
  Box,
  IconButton,
  Alert,
  Typography,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff, ErrorOutline } from "@mui/icons-material";
import { CustomTextField } from "../../components/CustomTextField";
import { Button } from "../../components/Button";
import { FormField } from "../../components/FormField";

export function LoginForm() {
  const { login, accessDisabledMessage } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(accessDisabledMessage);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(""); // Clear error when user starts typing
  };

  const getErrorMessage = (errorType, backendMessage) => {
    switch (errorType) {
      case "unauthorized":
        if (backendMessage === "No active account found with the given credentials") {
          return "Credenciales inválidas. Verifica tu email y contraseña.";
        }
        return backendMessage || "Credenciales inválidas. Verifica tu email y contraseña.";
      case "network":
        return "Error de conexión. Verifica tu conexión a internet e inténtalo de nuevo.";
      case "invalid_response":
        return "Error del servidor. Inténtalo de nuevo más tarde.";
      default:
        return "Error al iniciar sesión. Inténtalo de nuevo.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.email.trim()) {
      setError("El email es requerido");
      return;
    }
    if (!formData.email.includes("@")) {
      setError("Por favor ingresa un email válido");
      return;
    }
    if (!formData.password.trim()) {
      setError("La contraseña es requerida");
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(getErrorMessage(result.error, result.message));
      }
    } catch {
      setError("Error al iniciar sesión. Inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "grid", rowGap: 2 }}
    >
      {/* Email */}
      <FormField label="Email" htmlFor="login-email">
        <CustomTextField
          id="login-email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder="tu@agencia.com"
          autoComplete="email"
          disabled={isLoading}
        />
      </FormField>

      {/* Password */}
      <FormField
        label="Contraseña"
        htmlFor="login-password"
        aside={
          <Link component={RouterLink} to="/password-reset" underline="hover" sx={{ fontSize: 12, fontWeight: 600 }}>
            ¿Has olvidado tu contraseña?
          </Link>
        }
      >
        <Box sx={{ position: "relative" }}>
          <CustomTextField
            id="login-password"
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            placeholder="Tu contraseña"
            disabled={isLoading}
            sx={{ paddingRight: "40px" }}
          />
          <IconButton
            aria-label={
              showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
            }
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
            sx={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              padding: "4px",
            }}
          >
            {showPassword ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </Box>
      </FormField>

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutline fontSize="small" />}
          sx={{ alignItems: "center" }}
        >
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        size="lg"
        fullWidth
        sx={{ mt: 1 }}
        disabled={isLoading}
      >
        {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
      </Button>
    </Box>
  );
}
